import React from 'react';
import PropTypes from 'prop-types';

class DiffViewer extends React.Component {
  static propTypes = {
    oldText: PropTypes.string.isRequired,
    newText: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      expandedBlocks: new Set(),
      expandAll: false,
    };
  }

  toggleBlock = (blockIndex) => {
    this.setState((prevState) => {
      const newExpanded = new Set(prevState.expandedBlocks);
      if (newExpanded.has(blockIndex)) {
        newExpanded.delete(blockIndex);
      } else {
        newExpanded.add(blockIndex);
      }
      return { expandedBlocks: newExpanded };
    });
  };

  toggleExpandAll = () => {
    this.setState((prevState) => ({ expandAll: !prevState.expandAll }));
  };

  render() {
    const diffOps = diffLines(this.props.oldText, this.props.newText);

    const changedOps = diffOps.filter((op) => op.type !== "equal");
    const addedCount = changedOps.filter((op) => op.type === "insert").length;
    const removedCount = changedOps.filter((op) => op.type === "delete").length;
    const totalChanged = addedCount + removedCount;

    // Group consecutive equal operations into blocks
    const groupedOps = [];
    let currentBlock = null;
    let blockIndex = 0;

    diffOps.forEach((op) => {
      if (op.type === "equal") {
        if (!currentBlock) {
          currentBlock = {
            type: "equal-block",
            ops: [],
            blockIndex: blockIndex++,
          };
        }
        currentBlock.ops.push(op);
      } else {
        if (currentBlock) {
          groupedOps.push(currentBlock);
          currentBlock = null;
        }
        groupedOps.push(op);
      }
    });
    if (currentBlock) {
      groupedOps.push(currentBlock);
    }

    return React.createElement(
      "div",
      { style: styles.container },
      React.createElement(
        "div",
        { style: styles.summary },
        React.createElement(
          "div",
          { style: styles.summaryLeft },
          React.createElement("strong", null, "Summary: "),
          React.createElement(
            "span",
            { style: styles.chipContainer },
            React.createElement(
              "span",
              { style: styles.chipChanged },
              totalChanged + " changed"
            ),
            React.createElement(
              "span",
              { style: styles.chipAdded },
              "+ " + addedCount
            ),
            React.createElement(
              "span",
              { style: styles.chipRemoved },
              "- " + removedCount
            )
          )
        ),
        React.createElement(
          "button",
          {
            style: styles.toggleButton,
            onClick: this.toggleExpandAll,
          },
          this.state.expandAll ? "Collapse All Unchanged" : "Expand All Unchanged"
        )
      ),

      React.createElement(
        "div",
        { style: styles.headerRow },
        React.createElement("div", { style: styles.lineNumberHeader }, "Old #"),
        React.createElement("div", { style: styles.codeHeader }, "Original"),
        React.createElement("div", { style: styles.lineNumberHeader }, "New #"),
        React.createElement("div", { style: styles.codeHeader }, "Modified")
      ),

      React.createElement(
        "div",
        null,
        groupedOps.map((item, idx) => {
          if (item.type === "equal-block") {
            const isExpanded = this.state.expandAll || this.state.expandedBlocks.has(item.blockIndex);
            const lineCount = item.ops.length;
            
            if (!isExpanded) {
              return React.createElement(
                "div",
                {
                  key: `block-${idx}`,
                  style: styles.collapsedBlock,
                },
                React.createElement(
                  "button",
                  {
                    style: styles.expandButton,
                    onClick: () => this.toggleBlock(item.blockIndex),
                  },
                  `⊕ Expand ${lineCount} unchanged line${lineCount !== 1 ? "s" : ""}`
                )
              );
            }

            return React.createElement(
              "div",
              { key: `block-${idx}` },
              React.createElement(
                "div",
                { style: styles.collapseButtonRow },
                React.createElement(
                  "button",
                  {
                    style: styles.collapseButton,
                    onClick: () => this.toggleBlock(item.blockIndex),
                  },
                  `⊖ Collapse ${lineCount} unchanged line${lineCount !== 1 ? "s" : ""}`
                )
              ),
              item.ops.map((op, opIdx) => {
                return React.createElement(
                  "div",
                  {
                    key: `${idx}-${opIdx}`,
                    style: rowStyleForOp(op),
                  },
                  React.createElement(
                    "div",
                    { style: styles.lineNumberCell },
                    op.oldLineNumber != null ? op.oldLineNumber : ""
                  ),
                  React.createElement(
                    "div",
                    { style: styles.codeCellDisabled },
                    op.oldText || ""
                  ),
                  React.createElement(
                    "div",
                    { style: styles.lineNumberCell },
                    op.newLineNumber != null ? op.newLineNumber : ""
                  ),
                  React.createElement(
                    "div",
                    { style: styles.codeCellDisabled },
                    op.newText || ""
                  )
                );
              })
            );
          } else {
            const op = item;
            return React.createElement(
              "div",
              {
                key: idx,
                style: rowStyleForOp(op),
              },
              React.createElement(
                "div",
                { style: styles.lineNumberCell },
                op.oldLineNumber != null ? op.oldLineNumber : ""
              ),
              React.createElement(
                "div",
                { style: styles.codeCellOld },
                op.oldText || ""
              ),
              React.createElement(
                "div",
                { style: styles.lineNumberCell },
                op.newLineNumber != null ? op.newLineNumber : ""
              ),
              React.createElement(
                "div",
                { style: styles.codeCellNew },
                op.newText || ""
              )
            );
          }
        })
      )
    );
  }
}

/* --------- diff + helpers (same as before, just plain JS) --------- */

function diffLines(oldText, newText) {
  const oldLines = oldText.split(/\r?\n/);
  const newLines = newText.split(/\r?\n/);
  const m = oldLines.length;
  const n = newLines.length;

  const dp = [];
  for (let i = 0; i <= m; i++) {
    dp[i] = [];
    for (let j = 0; j <= n; j++) {
      dp[i][j] = 0;
    }
  }

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const opsReversed = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      opsReversed.push({ type: "equal", oldIndex: i - 1, newIndex: j - 1 });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      opsReversed.push({ type: "insert", newIndex: j - 1 });
      j--;
    } else if (i > 0) {
      opsReversed.push({ type: "delete", oldIndex: i - 1 });
      i--;
    }
  }

  const basicOps = opsReversed.reverse();

  const result = [];
  let oldLineNumber = 1;
  let newLineNumber = 1;

  basicOps.forEach((op) => {
    if (op.type === "equal") {
      result.push({
        type: "equal",
        oldLineNumber: oldLineNumber,
        newLineNumber: newLineNumber,
        oldText: oldLines[op.oldIndex],
        newText: newLines[op.newIndex],
      });
      oldLineNumber++;
      newLineNumber++;
    } else if (op.type === "delete") {
      result.push({
        type: "delete",
        oldLineNumber: oldLineNumber,
        newLineNumber: null,
        oldText: oldLines[op.oldIndex],
        newText: null,
      });
      oldLineNumber++;
    } else if (op.type === "insert") {
      result.push({
        type: "insert",
        oldLineNumber: null,
        newLineNumber: newLineNumber,
        oldText: null,
        newText: newLines[op.newIndex],
      });
      newLineNumber++;
    }
  });

  return result;
}

const styles = {
  container: {
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 14,
    border: "1px solid #ddd",
    borderRadius: 6,
    overflow: "hidden",
    width: "100%",
    boxSizing: "border-box",
  },
  summary: {
    padding: "8px 12px",
    borderBottom: "1px solid #eee",
    background: "#f7f7f7",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  summaryLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  chipContainer: {
    display: "flex",
    gap: "8px",
  },
  chipChanged: {
    display: "inline-block",
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 12,
    background: "#e3f2fd",
    color: "#1976d2",
    border: "1px solid #90caf9",
  },
  chipAdded: {
    display: "inline-block",
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 12,
    background: "#e6ffed",
    color: "#0e7e2f",
    border: "1px solid #9be9a8",
  },
  chipRemoved: {
    display: "inline-block",
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 12,
    background: "#ffeef0",
    color: "#c2185b",
    border: "1px solid #ffcdd2",
  },
  toggleButton: {
    padding: "4px 12px",
    fontSize: 12,
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 4,
    cursor: "pointer",
    color: "#333",
    fontWeight: 500,
  },
  collapsedBlock: {
    padding: "8px 12px",
    borderBottom: "1px solid #f0f0f0",
    background: "#f9f9f9",
    textAlign: "center",
  },
  expandButton: {
    padding: "4px 16px",
    fontSize: 13,
    background: "#fff",
    border: "1px solid #ccc",
    borderRadius: 4,
    cursor: "pointer",
    color: "#666",
    fontWeight: 500,
  },
  collapseButtonRow: {
    padding: "4px 12px",
    borderBottom: "1px solid #f0f0f0",
    background: "#f5f5f5",
    textAlign: "center",
  },
  collapseButton: {
    padding: "3px 12px",
    fontSize: 12,
    background: "#fff",
    border: "1px solid #ccc",
    borderRadius: 4,
    cursor: "pointer",
    color: "#666",
    fontWeight: 500,
  },
  headerRow: {
    display: "grid",
    gridTemplateColumns: "80px 1fr 80px 1fr",
    borderBottom: "1px solid #eee",
    background: "#fafafa",
    fontWeight: 600,
  },
  lineNumberHeader: {
    padding: "6px 8px",
    borderRight: "1px solid #eee",
    textAlign: "center",
  },
  codeHeader: {
    padding: "6px 8px",
    borderRight: "1px solid #eee",
  },
  lineNumberCell: {
    padding: "4px 8px",
    borderRight: "1px solid #eee",
    textAlign: "right",
    color: "#999",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  codeCellOld: {
    padding: "4px 8px",
    borderRight: "1px solid #eee",
    fontFamily:
      "SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    background: "#ffeef0",
  },
  codeCellNew: {
    padding: "4px 8px",
    fontFamily:
      "SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    background: "#e6ffed",
  },
  codeCellDisabled: {
    padding: "4px 8px",
    borderRight: "1px solid #eee",
    fontFamily:
      "SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: "#999",
    background: "#fafafa",
    opacity: 0.6,
  },
};

function rowStyleForOp(op) {
  return {
    display: "grid",
    gridTemplateColumns: "80px 1fr 80px 1fr",
    borderBottom: "1px solid #f0f0f0",
  };
}

export default DiffViewer;

